import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { alpha, Box, Paper, Stack, Typography } from '@mui/material';
import React, { useContext, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CallAgentTable from '../../components/agents/CallAgentTable/CallAgentTable';
import ChatAgentTable from '../../components/agents/ChatAgentTable/ChatAgentTable';
import StyledButton from '../../components/shared/Button/StyledButton';
import Page from '../../components/shared/Page/Page';
import Tabs from '../../components/shared/Tabs/Tabs';
import AuthContext from '../../contexes/AuthContext';
import { useSubscription } from '../../contexes/SubscriptionProvider';
import { getSingleLinePaidPlanAgentsVisibility } from '../../helpers/billingPlanSwitchUi';
import useCompany from '../../hooks/useCompany';
import { useSnackBar } from '../../hooks/useSnackBar';
import { useTranslation } from '../../hooks/useTranslation';
import { DeviceWidth } from '../../interfaces/App';
import { getUser } from '../../services/userService';

type AgentsBundleUpsellMessageKey =
  | 'Agents page bundle upgrade for call agents'
  | 'Agents page bundle upgrade for chat agents';

function AgentsBundleUpsellPanel({
  messageKey,
}: {
  messageKey: AgentsBundleUpsellMessageKey;
}) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        py: { xs: 2, sm: 3 },
        px: { xs: 1, sm: 2 },
        boxSizing: 'border-box',
      }}
    >
      <Paper
        elevation={0}
        sx={theme => ({
          width: '100%',
          maxWidth: 720,
          p: 2.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.info.light}`,
          bgcolor: alpha(theme.palette.info.main, 0.08),
          boxSizing: 'border-box',
        })}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            sx={{ minWidth: 0, flex: 1 }}
          >
            <InfoOutlined color="info" sx={{ flexShrink: 0, mt: 0.25 }} />
            <Typography variant="body2" color="text.primary">
              {t(messageKey)}
            </Typography>
          </Stack>
          <Box
            component={Link}
            to="/pricing?PlanTab=bundle"
            sx={{
              textDecoration: 'none',
              flexShrink: 0,
              alignSelf: { xs: 'stretch', sm: 'center' },
            }}
          >
            <StyledButton
              variant="contained"
              color="primary"
              size="small"
              sx={{
                height: 'auto',
                minHeight: 44,
                py: 1.25,
                px: 2,
                lineHeight: 1.25,
                whiteSpace: 'normal',
                textAlign: 'center',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {t('View bundle plans')}
            </StyledButton>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}

const Agents: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser, setCurrentUser } = useContext(AuthContext);
  const { companyDetails } = useCompany(currentUser);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshSubscription, subscription } = useSubscription();
  const showSnackBar = useSnackBar();

  const { hideCallAgentsList, hideChatAgentsList } = useMemo(
    () => getSingleLinePaidPlanAgentsVisibility(companyDetails, subscription),
    [companyDetails, subscription]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('subscribed') === '1') {
      // Refresh subscription and user
      (async () => {
        await refreshSubscription();
        if (currentUser) {
          const userResponse = await getUser(currentUser.id);
          setCurrentUser(userResponse.message);
        }
        showSnackBar(
          t('Congrats on your new plan selection! - The Elora Team'),
          'success'
        );
        // Remove the query param from the URL
        navigate(location.pathname, { replace: true });
      })();
    }
  }, [
    location.search,
    currentUser,
    refreshSubscription,
    setCurrentUser,
    showSnackBar,
    navigate,
    location.pathname,
    t,
  ]);

  return (
    <Page title="Elora | Agents">
      <Box
        sx={{
          width: '100%',
          maxWidth: DeviceWidth.LG,
          margin: '0 auto',
          px: { xs: 2, sm: 2 },
          py: { xs: 1, md: 0 },
          boxSizing: 'border-box',
        }}
      >
        <Tabs
          labels={[t('Call Agents'), t('Chat Agents')]}
          contents={[
            <Box key="call-agents-tab-content" sx={{ mt: 2 }}>
              {hideCallAgentsList ? (
                <AgentsBundleUpsellPanel
                  key="call-agents-upsell"
                  messageKey="Agents page bundle upgrade for call agents"
                />
              ) : (
                <CallAgentTable
                  companyDetails={companyDetails}
                  key="call-agents"
                />
              )}
            </Box>,
            <Box key="chat-agents-tab-content" sx={{ mt: 2 }}>
              {hideChatAgentsList ? (
                <AgentsBundleUpsellPanel
                  key="chat-agents-upsell"
                  messageKey="Agents page bundle upgrade for chat agents"
                />
              ) : (
                <ChatAgentTable
                  companyDetails={companyDetails}
                  key="chat-agents"
                />
              )}
            </Box>,
          ]}
        />
      </Box>
    </Page>
  );
};

export default Agents;
